import React, { memo, useEffect, useRef, useState } from 'react';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import {
  Chip,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@material-ui/core';
import {
  Check, Delete, Edit, ExpandMore,
} from '@material-ui/icons';
import { normalizeSynonymTerms } from 'slices/SynonymsSlice';
import DirectionIcon from './DirectionIcon';
import styles from '../../Synonyms.module.scss';
import tableStyles from 'components/kea/tables/DataTable.module.scss';

const toInputValue = (values) => normalizeSynonymTerms(values).join(', ');
const termsEqual = (left, right) => (
  normalizeSynonymTerms(left).join('\u0000') === normalizeSynonymTerms(right).join('\u0000')
);
const editSnapshot = synonym => ({
  left: synonym.left,
  right: synonym.right,
  bidirectional: synonym.bidirectional,
  enabled: synonym.enabled,
  _isModified: Boolean(synonym._isModified),
});

const SynonymRow = memo(({
  synonym,
  isHighlighted = false,
  shouldFocus = false,
  showValidationErrors = false,
  onUpdate,
  onDelete,
}) => {
  const localizeMessage = useLocalizedMessage();
  const leftInputRef = useRef(null);
  const rightInputRef = useRef(null);
  const requestedFocusRef = useRef(shouldFocus ? 'left' : null);
  const wasModifiedRef = useRef(Boolean(synonym._isModified));
  const hasAcceptedRef = useRef(!synonym._isNew);
  const editSnapshotRef = useRef(editSnapshot(synonym));
  const [isEditing, setIsEditing] = useState(Boolean(isHighlighted || synonym._isNew));
  const [leftDraft, setLeftDraft] = useState(() => toInputValue(synonym.left));
  const [rightDraft, setRightDraft] = useState(() => toInputValue(synonym.right));

  const leftTerms = normalizeSynonymTerms(leftDraft);
  const rightTerms = normalizeSynonymTerms(rightDraft);
  const leftInvalid = leftTerms.length === 0;
  const rightInvalid = rightTerms.length === 0;

  useEffect(() => {
    if (showValidationErrors && (leftInvalid || rightInvalid)) setIsEditing(true);
  }, [showValidationErrors, leftInvalid, rightInvalid]);

  useEffect(() => {
    const changesWereReset = wasModifiedRef.current && !synonym._isModified;
    if (changesWereReset) {
      setLeftDraft(toInputValue(synonym.left));
      setRightDraft(toInputValue(synonym.right));
      setIsEditing(false);
    }
    wasModifiedRef.current = Boolean(synonym._isModified);
  }, [synonym._id, synonym._isModified, synonym.left, synonym.right]);

  useEffect(() => {
    if (!isEditing || !requestedFocusRef.current) return undefined;
    const target = requestedFocusRef.current === 'right' ? rightInputRef : leftInputRef;
    const timer = window.setTimeout(() => {
      if (target.current) {
        target.current.focus();
        target.current.select();
      }
      requestedFocusRef.current = null;
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isEditing, shouldFocus, synonym._id]);

  const commitDraft = (field, value) => {
    const nextTerms = normalizeSynonymTerms(value);
    if (!termsEqual(nextTerms, synonym[field])) {
      onUpdate(synonym._id, { [field]: nextTerms });
    }
  };

  const handleTextChange = (field, setDraft) => (event) => {
    const { value } = event.target;
    setDraft(value);
    commitDraft(field, value);
  };

  const beginEditing = (field = 'left') => {
    editSnapshotRef.current = editSnapshot(synonym);
    setLeftDraft(toInputValue(synonym.left));
    setRightDraft(toInputValue(synonym.right));
    requestedFocusRef.current = field;
    setIsEditing(true);
  };

  const handleCancel = () => {
    const snapshot = editSnapshotRef.current;
    setLeftDraft(toInputValue(snapshot.left));
    setRightDraft(toInputValue(snapshot.right));

    if (synonym._isNew && !hasAcceptedRef.current) {
      onDelete(synonym._id);
      return;
    }

    onUpdate(synonym._id, snapshot);
    setIsEditing(false);
  };

  const handleDone = () => {
    const currentLeftDraft = leftInputRef.current ? leftInputRef.current.value : leftDraft;
    const currentRightDraft = rightInputRef.current ? rightInputRef.current.value : rightDraft;
    const currentLeftInvalid = normalizeSynonymTerms(currentLeftDraft).length === 0;
    const currentRightInvalid = normalizeSynonymTerms(currentRightDraft).length === 0;

    setLeftDraft(currentLeftDraft);
    setRightDraft(currentRightDraft);
    commitDraft('left', currentLeftDraft);
    commitDraft('right', currentRightDraft);

    if (currentLeftInvalid || currentRightInvalid) {
      requestedFocusRef.current = currentLeftInvalid ? 'left' : 'right';
      const target = currentLeftInvalid ? leftInputRef : rightInputRef;
      if (target.current) target.current.focus();
      return;
    }

    hasAcceptedRef.current = true;
    setIsEditing(false);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleDone();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      handleCancel();
    }
  };

  const renderTermsPreview = terms => (
    <div className={styles.termsPreview}>
      {terms.map((term) => (
        <Chip key={term} label={term} size="small" className={styles.termChip} />
      ))}
    </div>
  );

  return (
    <TableRow
      className={`${tableStyles.row} ${styles.tableRow} ${!synonym.enabled ? styles.rowDisabled : ''} ${isHighlighted ? styles.newRow : ''}`.trim()}
      data-testid={isHighlighted ? 'synonym-row-new' : 'synonym-row'}
      data-new-row={isHighlighted ? 'true' : undefined}
      data-editing={isEditing ? 'true' : 'false'}
    >
      <TableCell className={styles.termsCell}>
        {isEditing ? (
          <TextField
            className={styles.termsInput}
            inputRef={leftInputRef}
            value={leftDraft}
            onChange={handleTextChange('left', setLeftDraft)}
            onKeyDown={handleInputKeyDown}
            fullWidth
            variant="outlined"
            size="small"
            error={showValidationErrors && leftInvalid}
            helperText={showValidationErrors && leftInvalid
              ? localizeMessage('search.synonyms.validation.required')
              : localizeMessage('search.synonyms.validation.commaSeparated')}
            placeholder={localizeMessage('customization.synonyms.placeholder.commaSeparated')}
            inputProps={{ 'aria-label': localizeMessage('search.synonyms.table.left') }}
          />
        ) : renderTermsPreview(leftTerms)}
      </TableCell>

      <TableCell className={styles.relationCell}>
        <span className={styles.relationControl}>
          <Select
            className={styles.relationSelect}
            value={synonym.bidirectional ? 'both' : 'one'}
            onChange={(event) => onUpdate(synonym._id, {
              bidirectional: event.target.value === 'both',
            })}
            renderValue={(direction) => (
              <span className={`${styles.selectedDirectionIcon} ${direction === 'both'
                ? styles.relationBidirectional
                : styles.relationUnidirectional}`}
              >
                <DirectionIcon variant={direction === 'both' ? 'exchange' : 'forward'} />
              </span>
            )}
            IconComponent={ExpandMore}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
              transformOrigin: { vertical: 'top', horizontal: 'center' },
              getContentAnchorEl: null,
              classes: { paper: styles.relationMenu },
            }}
            disableUnderline
            onClick={(event) => event.stopPropagation()}
            inputProps={{ 'aria-label': localizeMessage('search.synonyms.table.direction') }}
          >
            <MenuItem value="both" className={styles.relationOption}>
              <span className={`${styles.relationOptionIcon} ${styles.relationBidirectional}`}>
                <DirectionIcon variant="exchange" />
              </span>
              <span>{localizeMessage('search.synonyms.bidirectional.label')}</span>
            </MenuItem>
            <MenuItem value="one" className={styles.relationOption}>
              <span className={`${styles.relationOptionIcon} ${styles.relationUnidirectional}`}>
                <DirectionIcon variant="forward" />
              </span>
              <span>{localizeMessage('search.synonyms.unidirectional.label')}</span>
            </MenuItem>
          </Select>
        </span>
      </TableCell>

      <TableCell className={styles.termsCell}>
        {isEditing ? (
          <TextField
            className={styles.termsInput}
            inputRef={rightInputRef}
            value={rightDraft}
            onChange={handleTextChange('right', setRightDraft)}
            onKeyDown={handleInputKeyDown}
            fullWidth
            variant="outlined"
            size="small"
            error={showValidationErrors && rightInvalid}
            helperText={showValidationErrors && rightInvalid
              ? localizeMessage('search.synonyms.validation.required')
              : localizeMessage('search.synonyms.validation.commaSeparated')}
            placeholder={localizeMessage('customization.synonyms.placeholder.commaSeparated')}
            inputProps={{ 'aria-label': localizeMessage('search.synonyms.table.right') }}
          />
        ) : renderTermsPreview(rightTerms)}
      </TableCell>

      <TableCell className={styles.statusCell}>
        <label className={styles.switchLabel} onClick={(event) => event.stopPropagation()}>
          <Switch
            checked={synonym.enabled}
            onChange={(event) => onUpdate(synonym._id, { enabled: event.target.checked })}
            color="primary"
            inputProps={{ 'aria-label': localizeMessage('search.synonyms.table.status') }}
          />
          <span className={`${styles.statusLabel} ${synonym.enabled ? styles.statusEnabled : ''}`.trim()}>
            {localizeMessage(synonym.enabled
              ? 'search.synonyms.status.enabled'
              : 'search.synonyms.status.disabled')}
          </span>
        </label>
      </TableCell>

      <TableCell className={styles.actionsCell}>
        <div className={styles.rowActions}>
          {isEditing && !synonym._isNew && (
            <Tooltip title={localizeMessage('search.synonyms.actions.done')}>
              <span>
                <IconButton
                  className={styles.doneButton}
                  onClick={handleDone}
                  disabled={leftInvalid || rightInvalid}
                  aria-label={localizeMessage('search.synonyms.actions.done')}
                >
                  <Check fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!isEditing && (
            <Tooltip title={localizeMessage('search.synonyms.actions.edit')}>
              <IconButton
                className={styles.editButton}
                onClick={(event) => {
                  event.stopPropagation();
                  beginEditing('left');
                }}
                aria-label={localizeMessage('search.synonyms.actions.edit')}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={localizeMessage('search.synonyms.actions.delete')}>
            <IconButton
              className={styles.deleteButton}
              onClick={(event) => {
                event.stopPropagation();
                onDelete(synonym._id);
              }}
              aria-label={localizeMessage('search.synonyms.actions.delete')}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </TableCell>
    </TableRow>
  );
});

export default SynonymRow;
